import { authenticatedFetch } from '../api';

export type PublishPlanResponse = {
  execution_enabled: false;
  gate: {
    run_id: string | null;
    current: boolean;
    passed: boolean;
  };
  approval: {
    approved: boolean;
    approval_id: string | null;
    approved_at: string | null;
    approved_by_current_user: boolean;
    prior_approval_exists: boolean;
  };
  plan: {
    supported: boolean;
    catalog_id: string | null;
    price_list_id: string | null;
    required_scopes: string[];
    mutations: Array<{
      kind: string;
      variables: Record<string, unknown>;
      description: string;
    }>;
    blockers: string[];
    warnings: string[];
    fingerprint: string;
  };
};

export type PublishExecutionSummary = {
  id: string;
  status: string;
  plan_fingerprint: string;
  mutation_count: number;
  applied_mutations: number;
  error: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  recovery_review_recommended: boolean;
};

export type PublishExecutionHistoryResponse = {
  execution_enabled: false;
  items: PublishExecutionSummary[];
};

export type PublishRecoveryResponse = {
  read_only: true;
  actions_enabled: false;
  execution: {
    id: string;
    status: string;
    error: string | null;
    plan_fingerprint: string;
  };
  shopify: {
    price_list_id: string;
    classification: 'desired_state' | 'baseline_state' | 'diverged_state' | 'unavailable';
    baseline: { type: string | null; value: string | null };
    desired: { type: string | null; value: string | null };
    observed: { type: string | null; value: string | null } | null;
  };
  atellius: {
    approved_version_id: string;
    approved_version_number: number;
    approved_version_state: string;
    current_published_version_number: number | null;
  };
  recommendation: string;
};

async function responseError(response: Response, fallback: string): Promise<Error> {
  const body = (await response.json().catch(() => null)) as { detail?: string } | null;
  return new Error(body?.detail ?? `${fallback} (${response.status})`);
}

export async function getPublishPlan(policyId: string): Promise<PublishPlanResponse> {
  const response = await authenticatedFetch(`/api/publish/policies/${policyId}/plan`);
  if (!response.ok) {
    throw await responseError(response, 'Could not compile publish plan');
  }
  return (await response.json()) as PublishPlanResponse;
}

export async function approvePublishPlan(
  policyId: string,
  planFingerprint: string,
): Promise<PublishPlanResponse> {
  const response = await authenticatedFetch(`/api/publish/policies/${policyId}/approval`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      plan_fingerprint: planFingerprint,
      confirmed: true,
    }),
  });
  if (!response.ok) {
    throw await responseError(response, 'Could not approve publish plan');
  }
  return (await response.json()) as PublishPlanResponse;
}

export async function revokePublishApproval(policyId: string): Promise<void> {
  const response = await authenticatedFetch(`/api/publish/policies/${policyId}/approval`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw await responseError(response, 'Could not revoke publish approval');
  }
}

export async function getPublishExecutionHistory(
  policyId: string,
): Promise<PublishExecutionHistoryResponse> {
  const response = await authenticatedFetch(`/api/publish/policies/${policyId}/executions`);
  if (!response.ok) {
    throw await responseError(response, 'Could not load publish execution history');
  }
  return (await response.json()) as PublishExecutionHistoryResponse;
}

export async function inspectPublishRecovery(executionId: string): Promise<PublishRecoveryResponse> {
  const response = await authenticatedFetch(`/api/publish/executions/${executionId}/recovery`);
  if (!response.ok) {
    throw await responseError(response, 'Could not inspect publish recovery state');
  }
  return (await response.json()) as PublishRecoveryResponse;
}
